

import React, { useState, useRef, useEffect, createContext, useContext } from 'react';
import { useLocation } from 'react-router-dom';

// Website data context
const WebsiteDataContext = createContext({});

// Website data provider component
export const WebsiteDataProvider = ({ children }) => {
    const location = useLocation();
    const [websiteData, setWebsiteData] = useState({});

    useEffect(() => {
        // Gather job portal specific data
        const pageTitle = document.title;
        const mainContent = document.querySelector('main')?.textContent ||
            document.body.textContent;

        // Look for job-specific information on the page
        const jobTitle = document.querySelector('[data-job-title]')?.textContent || '';
        const companyName = document.querySelector('[data-company-name]')?.textContent || '';
        const jobDescription = document.querySelector('[data-job-description]')?.textContent || '';

        setWebsiteData({
            path: location.pathname,
            title: pageTitle,
            content: mainContent?.slice(0, 1000) || '',
            jobInfo: {
                title: jobTitle,
                company: companyName,
                description: jobDescription,
            },
            pageType: getPageType(location.pathname)
        });
    }, [location]);

    // Helper to determine what type of page we're on
    const getPageType = (path) => {
        if (path === '/') return 'home';
        if (path.includes('/jobs')) return 'jobs';
        if (path.includes('/description')) return 'job-description';
        if (path.includes('/company')) return 'company';
        if (path.includes('/profile')) return 'profile';
        if (path.includes('/admin')) return 'admin';
        if (path.includes('/signup') || path.includes('/login')) return 'auth';
        return 'other';
    };

    return (
        <WebsiteDataContext.Provider value={websiteData}>
            {children}
        </WebsiteDataContext.Provider>
    );
};

// Hook to use website data
const useWebsiteData = () => useContext(WebsiteDataContext);

// Reusable AI sparkle icon (reads as "AI assistant" rather than a generic robot/chip)
const AiSparkleIcon = ({ className }) => (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className={className}>
        <path d="M12 2.5c.3 0 .55.2.62.48l1.2 4.68 4.68 1.2a.64.64 0 010 1.24l-4.68 1.2-1.2 4.68a.64.64 0 01-1.24 0l-1.2-4.68-4.68-1.2a.64.64 0 010-1.24l4.68-1.2 1.2-4.68c.07-.28.32-.48.62-.48z" />
        <path d="M18.5 15c.24 0 .45.16.5.4l.4 1.7 1.7.4a.5.5 0 010 .98l-1.7.4-.4 1.7a.5.5 0 01-1 0l-.4-1.7-1.7-.4a.5.5 0 010-.98l1.7-.4.4-1.7c.05-.24.26-.4.5-.4z" />
    </svg>
);

// Small line icons for quick actions (kept monochrome so they read as UI, not decoration)
const icons = {
    resume: (p) => (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" className={p}>
            <path d="M7 3.5h7l3.5 3.5V20a1 1 0 01-1 1H7a1 1 0 01-1-1V4.5a1 1 0 011-1z" strokeLinejoin="round" />
            <path d="M14 3.5V7h3.5" strokeLinejoin="round" />
            <path d="M8.5 12h7M8.5 15h7M8.5 9h3" strokeLinecap="round" />
        </svg>
    ),
    jobs: (p) => (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" className={p}>
            <path d="M4 8.5A1.5 1.5 0 015.5 7h13A1.5 1.5 0 0120 8.5V18a1.5 1.5 0 01-1.5 1.5h-13A1.5 1.5 0 014 18V8.5z" />
            <path d="M8.5 7V5.5A1.5 1.5 0 0110 4h4a1.5 1.5 0 011.5 1.5V7M4 12h16" />
        </svg>
    ),
    ats: (p) => (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" className={p}>
            <path d="M4 19V5M4 19h16M8 19v-5M12.5 19V9M17 19v-8" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
    ),
    letter: (p) => (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" className={p}>
            <path d="M4 6.5A1.5 1.5 0 015.5 5h13A1.5 1.5 0 0120 6.5v11A1.5 1.5 0 0118.5 19h-13A1.5 1.5 0 014 17.5v-11z" />
            <path d="M4.5 6.5L12 12.5l7.5-6" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
    ),
    interview: (p) => (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" className={p}>
            <path d="M8 10.5a4 4 0 118 0c0 2.5-2 3-2 4.5H10c0-1.5-2-2-2-4.5z" strokeLinecap="round" strokeLinejoin="round" />
            <path d="M10.5 17.5h3" strokeLinecap="round" />
        </svg>
    ),
    career: (p) => (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" className={p}>
            <path d="M4 19l5-5 3.5 3 6.5-7.5" strokeLinecap="round" strokeLinejoin="round" />
            <path d="M15.5 8.5H20V13" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
    ),
};

// Attach / file / remove icons for the resume upload feature
const PaperclipIcon = ({ className }) => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" className={className}>
        <path d="M17.5 8.5l-7.6 7.6a2.6 2.6 0 003.7 3.7l7.4-7.4a4.6 4.6 0 00-6.5-6.5l-7.4 7.4a6.6 6.6 0 009.3 9.3" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
);

const FileIcon = ({ className }) => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" className={className}>
        <path d="M7 3.5h7l3.5 3.5V20a1 1 0 01-1 1H7a1 1 0 01-1-1V4.5a1 1 0 011-1z" strokeLinejoin="round" />
        <path d="M14 3.5V7h3.5" strokeLinejoin="round" />
    </svg>
);

const RemoveIcon = ({ className }) => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className={className}>
        <path d="M6 6l12 12M6 18L18 6" strokeLinecap="round" />
    </svg>
);

// File types the model can actually read (images + PDFs). Word docs aren't
// natively understood by Gemini's inline_data, so we don't advertise them.
const ACCEPTED_FILE_TYPES = ['application/pdf', 'image/png', 'image/jpeg', 'image/webp'];
const MAX_FILE_SIZE_MB = 8;

// Reads a File into a base64 string (without the data: prefix) for inline_data
const fileToBase64 = (file) =>
    new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result.split(',')[1]);
        reader.onerror = () => reject(new Error('Could not read the file'));
        reader.readAsDataURL(file);
    });

// ChatBot component
const ChatBot = () => {
    const [isOpen, setIsOpen] = useState(false);
    const [showPopup, setShowPopup] = useState(false);
    const [messages, setMessages] = useState([
        {
            role: "assistant",
            content: "Hi, I'm Hireees AI — your career assistant. I can review your resume, match you to open roles, improve your ATS score, draft a cover letter, or help you prep for an interview.\n\nWhat would you like to start with?"
        }
    ]);
    const [input, setInput] = useState('');
    const [loading, setLoading] = useState(false);
    const [attachedFile, setAttachedFile] = useState(null); // { name, mimeType, sizeLabel, base64, previewUrl }
    const [fileError, setFileError] = useState('');
    const messagesEndRef = useRef(null);
    const fileInputRef = useRef(null);
    const websiteData = useWebsiteData();

    // Get API key from environment
    // NOTE: Vite only exposes env vars to the client if they are prefixed with VITE_
    // Make sure your .env file has: VITE_GEMINI_API_KEY=your_key_here
    const API_KEY = import.meta.env.VITE_GEMINI_API_KEY || '';

    // Auto-scroll to bottom of messages
    useEffect(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, [messages]);

    // Show welcome popup after a delay when the site loads
    useEffect(() => {
        const timer = setTimeout(() => {
            setShowPopup(true);
            // Auto-hide popup after 8 seconds if user doesn't interact
            const hideTimer = setTimeout(() => {
                setShowPopup(false);
            }, 8000);

            return () => clearTimeout(hideTimer);
        }, 2000); // 2 second delay before showing popup

        return () => clearTimeout(timer);
    }, []);

    const getWelcomeMessage = () => {
        // Customize welcome message based on page type
        const pageType = websiteData.pageType;

        if (pageType === 'job-description') {
            return "Looking at this role? I can help tailor your application.";
        } else if (pageType === 'jobs') {
            return "Searching for the right fit? I can help narrow it down.";
        } else if (pageType === 'admin') {
            return "Need help with a posting or candidate review?";
        } else if (pageType === 'home') {
            return "Looking for your next role? I can help you find it.";
        }

        return "Have a question about your job search?";
    };

    const handleSendMessage = async (quickMessage = null) => {
       const message = quickMessage || input;
       const fileToSend = attachedFile;

if (!message.trim() && !fileToSend) return;

        // Add user message (include file metadata so the bubble can show a chip/thumbnail)
        setMessages(prev => [...prev, {
            role: 'user',
            content: message || (fileToSend ? `Uploaded ${fileToSend.name}` : ''),
            file: fileToSend ? { name: fileToSend.name, mimeType: fileToSend.mimeType, previewUrl: fileToSend.previewUrl } : null,
        }]);
        setInput('');
        setAttachedFile(null);
        setLoading(true);

        try {
            // Check if API key is available
            if (!API_KEY) {
                throw new Error('Gemini API key is not configured');
            }

            // Create job context message
            let contextMessage = `You are Hireees AI, an assistant for a job portal website. `;
            contextMessage += `Current page: ${websiteData.title} (${websiteData.path}). `;

            if (websiteData.pageType === 'job-description' && websiteData.jobInfo.title) {
                contextMessage += `You're viewing a job: ${websiteData.jobInfo.title} at ${websiteData.jobInfo.company}. `;
            } else if (websiteData.pageType === 'jobs') {
                contextMessage += `You're browsing job listings. `;
            } else if (websiteData.pageType === 'admin') {
                contextMessage += `You're in the admin/recruiter section. `;
            }

            if (fileToSend) {
                contextMessage += `The user has attached a file named "${fileToSend.name}" (likely their resume) — read it and use it to inform your answer. `;
            }

            contextMessage += `User question: ${message || 'Please review the attached file.'}`;

            // Build the parts array: text plus, if present, the file as inline_data
            const parts = [{ text: contextMessage }];
            if (fileToSend) {
                parts.push({
                    inline_data: {
                        mime_type: fileToSend.mimeType,
                        data: fileToSend.base64,
                    },
                });
            }

            // Create the request payload using the exact format from the curl command
            const payload = {
                contents: [{ parts }]
            };

            // Use the gemini-2.0-flash model as shown in the curl command
           const endpoint = `https://generativelanguage.googleapis.com/v1beta/models/gemini-2.5-flash:generateContent?key=${API_KEY}`;

            console.log("Sending request to Gemini API...");

            const response = await fetch(endpoint, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(payload),
            });

            if (!response.ok) {
    const errorData = await response.json();

    console.log("FULL ERROR:", errorData);

    throw new Error(
        `API error: ${errorData.error?.message || response.statusText}`
    );
}

            const data = await response.json();


            // Extract the response text from the API response
            let responseText = "";

            if (data.candidates && data.candidates[0] && data.candidates[0].content) {
                // Extract text from parts
                const parts = data.candidates[0].content.parts;
                if (parts && parts.length > 0) {
                    responseText = parts.map(part => part.text).join(" ");
                }
            }

            if (!responseText) {
                responseText = "I'm sorry, I couldn't generate a response.";
            }

            setMessages(prev => [...prev, { role: 'assistant', content: responseText }]);
        } catch (error) {
            console.error('Error with Gemini API:', error);

            // Display a user-friendly error message
            let errorMessage = "I'm having trouble connecting to the AI service right now.";

            if (error.message.includes('API key')) {
                errorMessage = "The chatbot couldn't authenticate with the AI service. Please check your API key.";
            } else if (error.message.includes('quota')) {
                errorMessage = "You've reached the limit for the free Gemini API tier. Please try again later.";
            } else if (error.message.includes('not found') || error.message.includes('not supported')) {
                errorMessage = "There's an issue with the AI model configuration. Please verify your API is set up correctly.";
            }

            setMessages(prev => [...prev, {
                role: 'assistant',
                content: errorMessage
            }]);
        } finally {
            setLoading(false);
        }
    };

    // Handle enter key press
    const handleKeyPress = (e) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            handleSendMessage();
        }
    };

    // Open chat when clicking on welcome popup
    const handlePopupClick = () => {
        setIsOpen(true);
        setShowPopup(false);
    };

    const quickActions = [
        { key: 'resume', label: 'Review resume', icon: icons.resume },
        { key: 'jobs', label: 'Find jobs', icon: icons.jobs },
        { key: 'ats', label: 'ATS score', icon: icons.ats },
        { key: 'letter', label: 'Cover letter', icon: icons.letter },
        { key: 'interview', label: 'Interview prep', icon: icons.interview },
        { key: 'career', label: 'Career advice', icon: icons.career },
    ];

   const handleQuickAction = async (text) => {
    setInput(text);

    // Small delay so the input updates first
    setTimeout(() => {
        handleSendMessage(text);
    }, 100);
};

    // Handle a file being chosen from the file picker
    const handleFileSelect = async (e) => {
        const file = e.target.files?.[0];
        e.target.value = ''; // allow re-selecting the same file later
        if (!file) return;

        setFileError('');

        if (!ACCEPTED_FILE_TYPES.includes(file.type)) {
            setFileError('Please upload a PDF or an image (PNG, JPG, WEBP).');
            return;
        }
        if (file.size > MAX_FILE_SIZE_MB * 1024 * 1024) {
            setFileError(`File is too large. Max size is ${MAX_FILE_SIZE_MB}MB.`);
            return;
        }

        try {
            const base64 = await fileToBase64(file);
            setAttachedFile({
                name: file.name,
                mimeType: file.type,
                base64,
                previewUrl: file.type.startsWith('image/') ? URL.createObjectURL(file) : null,
            });
        } catch (err) {
            setFileError('Could not read that file. Please try again.');
        }
    };

    const removeAttachedFile = () => {
        setAttachedFile(null);
        setFileError('');
    };

    return (
        <div className="fixed bottom-14 right-10 z-50 flex flex-col items-end font-sans">
            {/* Welcome popup message */}
            {showPopup && !isOpen && (
                <div
                    onClick={handlePopupClick}
                    className="mb-4 bg-white rounded-xl shadow-lg shadow-slate-900/10 p-3.5 max-w-[260px] animate-fadeIn cursor-pointer border border-slate-200 hover:border-indigo-300 transition-all"
                >
                    <div className="flex items-start">
                        <div className="flex-shrink-0 bg-gradient-to-br from-indigo-600 to-violet-600 rounded-full p-1.5 mr-2.5">
                            <AiSparkleIcon className="h-4 w-4 text-white" />
                        </div>
                        <div>
                            <p className="text-slate-800 text-[13px] font-medium leading-snug">{getWelcomeMessage()}</p>
                            <p className="text-[12px] text-indigo-600 mt-1 font-medium">Chat with Hireees AI</p>
                        </div>
                    </div>
                </div>
            )}

            {/* Chat button */}
            <button
                onClick={() => {
                    setIsOpen(!isOpen);
                    setShowPopup(false);
                }}
                className="flex items-center justify-center w-14 h-14 rounded-full bg-gradient-to-br from-indigo-600 to-violet-600 text-white shadow-lg shadow-indigo-600/25 hover:shadow-xl hover:shadow-indigo-600/30 transition-all"
                aria-label={isOpen ? "Close chat" : "Open chat"}
            >
                {isOpen ? (
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                ) : (
                    <AiSparkleIcon className="h-6 w-6" />
                )}
            </button>

            {/* Chat window */}
            {isOpen && (
                <div className="absolute bottom-[68px] right-0 w-[340px] sm:w-[380px] h-[520px] bg-white rounded-2xl shadow-2xl shadow-slate-900/15 flex flex-col overflow-hidden border border-slate-200">
                    {/* Header */}
                    <div className="bg-gradient-to-r from-indigo-600 to-violet-600 text-white px-4 py-3.5 flex justify-between items-center flex-shrink-0">
                        <div className="flex items-center">
                            <div className="bg-white/15 rounded-full p-1.5 mr-2.5">
                                <AiSparkleIcon className="h-4 w-4" />
                            </div>
                            <div>
                                <h3 className="font-semibold text-[14px] leading-tight">Hireees AI</h3>
                                <p className="text-[11px] text-indigo-100/90 flex items-center gap-1 mt-0.5">
                                    <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 inline-block" />
                                    Online
                                </p>
                            </div>
                        </div>
                        <button
                            onClick={() => setIsOpen(false)}
                            className="text-white/80 hover:text-white transition-colors"
                            aria-label="Close chat"
                        >
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-4.5 w-4.5" width="18" height="18" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                        </button>
                    </div>

                    {/* Messages area */}
                    <div className="flex-1 px-3.5 py-4 overflow-y-auto bg-slate-50">

                        {messages.map((msg, idx) => (
                            <div
                                key={idx}
                                className={`mb-3 flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
                            >
                                <div
                                    className={`px-3.5 py-2.5 rounded-2xl max-w-[82%] text-[13.5px] leading-relaxed whitespace-pre-line ${msg.role === 'user'
                                            ? 'bg-indigo-600 text-white rounded-br-md'
                                            : 'bg-white text-slate-700 border border-slate-200 rounded-bl-md'
                                        }`}
                                >
                                    {msg.file && (
                                        <div className={`flex items-center gap-2 rounded-lg px-2 py-1.5 mb-1.5 ${msg.role === 'user' ? 'bg-white/15' : 'bg-slate-100'}`}>
                                            {msg.file.previewUrl ? (
                                                <img src={msg.file.previewUrl} alt={msg.file.name} className="w-8 h-8 rounded object-cover flex-shrink-0" />
                                            ) : (
                                                <span className={`flex-shrink-0 ${msg.role === 'user' ? 'text-white' : 'text-indigo-600'}`}><FileIcon className="w-5 h-5" /></span>
                                            )}
                                            <span className="text-[12px] font-medium truncate">{msg.file.name}</span>
                                        </div>
                                    )}
                                    {msg.content}
                                </div>
                            </div>
                        ))}

                        {messages.length === 1 && (
                            <div className="grid grid-cols-2 gap-1.5 mt-3">
                                {quickActions.map((item) => (
                                    <button
                                        key={item.key}
                                        onClick={() => handleQuickAction(item.label)}
                                        className="flex items-center gap-1.5 px-2.5 py-2 rounded-lg border border-slate-200 bg-white hover:border-indigo-300 hover:bg-indigo-50/60 text-[12.5px] text-slate-700 font-medium transition-colors text-left"
                                    >
                                        <span className="text-indigo-600 flex-shrink-0">{item.icon("h-3.5 w-3.5")}</span>
                                        <span className="truncate">{item.label}</span>
                                    </button>
                                ))}
                            </div>
                        )}

                        {loading && (
                            <div className="mb-3 flex justify-start">
                                <div className="bg-white border border-slate-200 rounded-2xl rounded-bl-md px-3.5 py-3 flex items-center gap-1">
                                    <span className="w-1.5 h-1.5 rounded-full bg-slate-400 animate-bounce [animation-delay:-0.3s]" />
                                    <span className="w-1.5 h-1.5 rounded-full bg-slate-400 animate-bounce [animation-delay:-0.15s]" />
                                    <span className="w-1.5 h-1.5 rounded-full bg-slate-400 animate-bounce" />
                                </div>
                            </div>
                        )}

                        <div ref={messagesEndRef} />
                    </div>

                    {/* Input area */}
                    <div className="border-t border-slate-200 p-3 bg-white flex-shrink-0">
                        {/* Attached file preview */}
                        {attachedFile && (
                            <div className="flex items-center gap-2 bg-slate-100 border border-slate-200 rounded-lg px-2.5 py-2 mb-2">
                                {attachedFile.previewUrl ? (
                                    <img src={attachedFile.previewUrl} alt={attachedFile.name} className="w-8 h-8 rounded object-cover flex-shrink-0" />
                                ) : (
                                    <span className="flex-shrink-0 text-indigo-600"><FileIcon className="w-5 h-5" /></span>
                                )}
                                <span className="text-[12px] text-slate-700 font-medium truncate flex-1">{attachedFile.name}</span>
                                <button
                                    onClick={removeAttachedFile}
                                    className="flex-shrink-0 text-slate-400 hover:text-slate-600"
                                    aria-label="Remove attachment"
                                >
                                    <RemoveIcon className="w-3.5 h-3.5" />
                                </button>
                            </div>
                        )}

                        {fileError && (
                            <p className="text-[11px] text-red-500 mb-1.5">{fileError}</p>
                        )}

                        <div className="flex items-center gap-2">
                            <input
                                ref={fileInputRef}
                                type="file"
                                accept={ACCEPTED_FILE_TYPES.join(',')}
                                onChange={handleFileSelect}
                                className="hidden"
                                aria-label="Upload resume"
                            />
                            <button
                                onClick={() => fileInputRef.current?.click()}
                                className="flex-shrink-0 w-9 h-9 rounded-full flex items-center justify-center text-slate-500 hover:text-indigo-600 hover:bg-indigo-50 transition-colors"
                                aria-label="Attach resume (PDF or image)"
                                title="Attach resume (PDF or image)"
                            >
                                <PaperclipIcon className="h-4.5 w-4.5" />
                            </button>
                            <input
                                type="text"
                                value={input}
                                onChange={(e) => setInput(e.target.value)}
                                onKeyPress={handleKeyPress}
                                placeholder={attachedFile ? "Add a note (optional)..." : "Ask about jobs, applications..."}
                                className="flex-1 min-w-0 border border-slate-300 rounded-full px-3.5 py-2 text-[13px] placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-indigo-500/40 focus:border-indigo-400"
                                aria-label="Chat message"
                            />
                            <button
                                onClick={() => handleSendMessage()}
                                disabled={loading || (!input.trim() && !attachedFile)}
                                className={`flex-shrink-0 w-9 h-9 rounded-full flex items-center justify-center transition-colors ${loading || (!input.trim() && !attachedFile)
                                        ? 'bg-slate-200 cursor-not-allowed text-slate-400'
                                        : 'bg-indigo-600 hover:bg-indigo-700 text-white'
                                    }`}
                                aria-label="Send message"
                            >
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
                                </svg>
                            </button>
                        </div>
                        <p className="text-[10.5px] text-slate-400 mt-1.5 text-center">AI responses may be inaccurate. Verify important details.</p>
                    </div>
                </div>
            )}

            {/* Add CSS animations */}
            <style jsx>{`
        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(10px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .animate-fadeIn {
          animation: fadeIn 0.5s ease-out forwards;
        }
      `}</style>
        </div>
    );
};

export default ChatBot;